<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <card-title-row class="mb-4" translation-path="sidebar.circularSectorSelection.title" :has-buttons="false" />
      <sidebar-row class="pt-2">
        <data-input v-model="pointGeometry" @input="handleParamInput" v-bind="geometryInput" />
        <sidebar-form v-model="params" :is-valid.sync="isFormValid" :inputs="inputs" :hasIconColumn="false">
          <template v-for="input in inputs" #[input.name]="data">
            <data-input v-bind="data" @input="handleParamInputDebounce" v-model="params[data.name]" :key="input.name" />
          </template>
        </sidebar-form>
      </sidebar-row>
    </v-col>
  </v-row>
</template>

<script>
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import SidebarRow from '@/components/SidebarRow';
import SidebarForm from '@/components/SidebarForm';
import { debounce } from 'lodash';

import { sync } from 'vuex-pathify';

export default {
  name: 'CircularSectorSelection',
  components: {
    CardTitleRow,
    DataInput,
    SidebarRow,
    SidebarForm,
  },
  data: () => ({
    pointGeometry: null,
    isFormValid: false,
    isCircularGeometryDrawn: false,
    isSelectionFetched: false,
    params: {
      azimuth: null,
      angle: null,
      radius: null,
    },
  }),
  watch: {
    isSelectionFormValid(nV) {
      this.isStickyButtonsValid = nV;
    },
    isSelectionFetched: {
      immediate: true,
      handler(nV) {
        this.stickyButtons = [
          {
            action: 'selectObjects',
            translationPath: 'sidebar.circularSectorSelection.selectObjects',
          },
          {
            action: 'resetSelection',
            translationPath: 'sidebar.circularSectorSelection.resetSelection',
            cancel: true,
            preventGoToLegendCancel: true,
            disabled: !nV,
            hasTooltip: false,
          },
        ];
      },
    },
  },
  methods: {
    handleParamInput() {
      if (this.isAllValid) {
        this.drawCircularSector();
      } else {
        this.clearCircularSector();
      }
    },
    drawCircularSector() {
      if (!this.isAllValid) return;
      this.$root.$emit('toggleCircularSector', true, this.params, this.pointGeometry?.coordinates);
      this.isCircularGeometryDrawn = true;
    },
    clearCircularSector() {
      this.$root.$emit('toggleCircularSector', false);
      this.isCircularGeometryDrawn = false;
    },
    async selectionHandler() {
      this.$root.$emit('selectFeaturesInCircularSector');
      this.isSelectionFetched = true;
      this.pointGeometry = null;
    },
    clearSelection() {
      this.deleteFeature(null, this.currentLayer?.id);
      this.isSelectionFetched = false;
    },
    deleteFeature(id, layerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
  },
  computed: {
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    currentLayer: sync('layers/currentLayer'),
    isAllValid() {
      return this.isFormValid && this.pointGeometry;
    },
    isSelectionFormValid() {
      return this.isAllValid && this.isCircularGeometryDrawn;
    },
    geometryInput() {
      return {
        name: 'circularSectorPoint',
        dataType: { name: 'geometry' },
        hasNameColumn: false,
        cols: '12',
        class: 'pb-4',
        buttonProps: { translationPath: 'sidebar.circularSectorSelection.selectPoint' },
        geometryType: 'Point',
        subtitleTranslationPath: 'sidebar.circularSectorSelection.selectPointSubtitle',
        rules: [v => !!v || this.$i18n.t('rules.required')],
      };
    },
    inputs() {
      return [
        {
          name: 'azimuth',
          cols: '7',
          allowNegative: false,
          nameCols: '5',
          dataType: { name: 'float' },
          showArrows: true,
          translationPath: 'sidebar.circularSectorSelection.azimuth',
          rules: [
            v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required'),
            v => v <= 360 || this.$i18n.t('sidebar.circularSectorSelection.azimuthRule'),
          ],
        },
        {
          name: 'angle',
          cols: '7',
          nameCols: '5',
          allowNegative: false,
          showArrows: true,
          dataType: { name: 'float' },
          translationPath: 'sidebar.circularSectorSelection.angle',
          rules: [
            v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required'),
            v => v <= 360 || this.$i18n.t('sidebar.circularSectorSelection.angleRule'),
          ],
        },
        {
          name: 'radius',
          cols: '7',
          nameCols: '5',
          showArrows: true,
          allowNegative: false,
          dataType: { name: 'float' },
          translationPath: 'sidebar.circularSectorSelection.radius',
          rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        },
      ];
    },
  },
  created() {
    this.handleParamInputDebounce = debounce(this.handleParamInput, 475);
  },
  mounted() {
    this.$root.$off('selectObjects');
    this.$root.$off('resetSelection');
    this.$root.$on('selectObjects', this.selectionHandler);
    this.$root.$on('resetSelection', this.clearSelection);
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = this.isSelectionFormValid;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.pointGeometry = null;
    this.clearCircularSector();
    this.clearSelection();
    this.isStickyButtonsVisible = false;
  },
};
</script>
